import { defineStore } from "pinia";
import apiService from "@/services/api";
import router from "@/router";
import { verifyMfaCode, resendMfaCode, type MfaMethod } from "@/services/mfaService";

export type LoginResult = "success" | "mfa" | "error";

interface Temple {
	id: string;
	name: string;
	slug: string;
	status: "PENDING" | "ACTIVE" | "SUSPENDED";
	onboardingCompleted: boolean;
	logoUrl?: string;
	primaryColor?: string;
	settings?: Record<string, any>;
}

interface TempleListItem {
	id: string;
	name: string;
	slug: string;
	role: string;
	primaryColor?: string;
}

interface User {
	id: string;
	accountId: string;
	email: string;
	displayName: string;
	religiousName?: string;
	title?: string;
	role: "OWNER" | "ADMIN" | "MEMBER";
	ritualFunction?: string;
	phone?: string;
	whatsappOptIn?: boolean;
	photoUrl?: string;
	tenantId: string;
	temple: Temple;
	needsOnboarding: boolean;
	completedTours?: Record<string, boolean>;
	subscriptionStatus?: string | null;
	/** Veredito do backend sobre o acesso — ver `isSubscriptionBlocked`. */
	subscriptionBlocked?: boolean;
	/** Até quando o acesso vale numa assinatura cancelada (fim do período pago). */
	subscriptionAccessUntil?: string | null;
	// Legacy compatibility
	username?: string;
}

interface AuthState {
	token: string | null;
	refreshToken: string | null;
	user: User | null;
	temples: TempleListItem[];
	loading: boolean;
	isLoggingOut: boolean;
	// MFA (autenticação em duas etapas) — estado do desafio durante o login
	mfaRequired: boolean;
	mfaMethod: MfaMethod | null;
	mfaChallengeToken: string | null;
}

export const useAuthStore = defineStore("auth", {
	state: (): AuthState => ({
		token: localStorage.getItem("token"),
		refreshToken: localStorage.getItem("refreshToken"),
		user: null,
		temples: [],
		loading: false,
		isLoggingOut: false,
		mfaRequired: false,
		mfaMethod: null,
		mfaChallengeToken: null,
	}),
	getters: {
		isLoggedIn: (state) => !!state.token,

		currentTemple: (state) => state.user?.temple || null,

		needsOnboarding: (state) => state.user?.needsOnboarding || false,

		isOwner: (state) => state.user?.role === "OWNER",

		isAdmin: (state) =>
			state.user?.role === "OWNER" || state.user?.role === "ADMIN",

		hasMultipleTemples: (state) => state.temples.length > 1,

		// Quem decide é o backend (`subscription-access.ts`): status cru não basta,
		// porque assinatura CANCELADA ainda dá acesso até o fim do período pago
		// (FR17). Repetir a conta aqui foi o que expulsou quem cancelou.
		isSubscriptionBlocked: (state) => state.user?.subscriptionBlocked === true,

		hasPermission: (state) => (permission: string) => {
			// Legacy compatibility - map role to permissions
			if (!state.user) return false;
			const role = state.user.role;
			if (role === "OWNER" || role === "ADMIN") {
				return true; // Admins have all permissions
			}
			// Check legacy permissions array if exists
			if (state.user.permissions) {
				return state.user.permissions.includes(permission);
			}
			return false;
		},

		hasAnyPermission: (state) => (permissions: string[]) => {
			if (!state.user || !permissions.length) return false;
			const role = state.user.role;
			if (role === "OWNER" || role === "ADMIN") return true;
			if (state.user.permissions) {
				return permissions.some((p) => state.user!.permissions!.includes(p));
			}
			return false;
		},

		hasAllPermissions: (state) => (permissions: string[]) => {
			if (!state.user || !permissions.length) return false;
			const role = state.user.role;
			if (role === "OWNER" || role === "ADMIN") return true;
			if (state.user.permissions) {
				return permissions.every((p) => state.user!.permissions!.includes(p));
			}
			return false;
		},
	},
	actions: {
		async login(email: string, password: string): Promise<LoginResult> {
			try {
				this.loading = true;
				this.resetMfaChallenge();

				const trustedDeviceToken =
					localStorage.getItem("trustedDeviceToken") || undefined;

				const response = await apiService.post("/auth/login", {
					email,
					password,
					trustedDeviceToken,
				});

				// MFA exigido: guardar o desafio e sinalizar para a tela de login
				if (response?.mfaRequired) {
					this.mfaRequired = true;
					this.mfaMethod = response.mfaMethod || null;
					this.mfaChallengeToken = response.mfaChallengeToken || null;
					return "mfa";
				}

				const ok = await this.completeLogin(response);
				return ok ? "success" : "error";
			} catch (error: any) {
				console.error("Login error:", error?.response?.data || error);
				return "error";
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Processa a resposta normal de login (tokens + dados do usuário).
		 * Reutilizado pelo login direto e pela verificação de MFA.
		 */
		async completeLogin(response: any): Promise<boolean> {
			if (response?.access_token) {
				this.token = response.access_token;
				if (this.token) localStorage.setItem("token", this.token);

				if (response.refresh_token) {
					this.refreshToken = response.refresh_token;
					if (this.refreshToken)
						localStorage.setItem("refreshToken", this.refreshToken);
				}
			} else if (response?.token) {
				// Legacy format
				this.token = response.token;
				this.refreshToken = response.refreshToken || null;

				if (this.token) localStorage.setItem("token", this.token);
				if (this.refreshToken) {
					localStorage.setItem("refreshToken", this.refreshToken);
				}
			} else {
				console.error("Formato de resposta desconhecido:", response);
				return false;
			}

			await this.fetchUser();
			await this.fetchTemples();

			return true;
		},

		/**
		 * Verifica o código MFA (TOTP, e-mail ou backup) e conclui o login.
		 * Quando rememberDevice, persiste o trustedDeviceToken para os próximos logins.
		 * Lança em caso de código inválido para a tela exibir a mensagem do backend.
		 */
		async verifyMfa(code: string, rememberDevice = false): Promise<boolean> {
			if (!this.mfaChallengeToken) return false;
			try {
				this.loading = true;
				const response = await verifyMfaCode({
					mfaChallengeToken: this.mfaChallengeToken,
					code,
					rememberDevice,
				});

				if (response?.trustedDeviceToken) {
					localStorage.setItem(
						"trustedDeviceToken",
						response.trustedDeviceToken
					);
				}

				const ok = await this.completeLogin(response);
				if (ok) this.resetMfaChallenge();
				return ok;
			} finally {
				this.loading = false;
			}
		},

		/**
		 * Reenvia o código MFA por e-mail (método EMAIL).
		 */
		async resendMfa(): Promise<void> {
			if (!this.mfaChallengeToken) return;
			await resendMfaCode(this.mfaChallengeToken);
		},

		resetMfaChallenge() {
			this.mfaRequired = false;
			this.mfaMethod = null;
			this.mfaChallengeToken = null;
		},

		async refreshAccessToken() {
			try {
				if (!this.refreshToken) {
					throw new Error("No refresh token available");
				}

				const response = await apiService.post("/auth/refresh-token", {
					refreshToken: this.refreshToken,
				});

				if (response.access_token) {
					this.token = response.access_token;
					if (this.token) localStorage.setItem("token", this.token);

					if (response.refresh_token) {
						this.refreshToken = response.refresh_token;
						if (this.refreshToken)
							localStorage.setItem("refreshToken", this.refreshToken);
					}
				} else if (response.token) {
					this.token = response.token;
					if (this.token) localStorage.setItem("token", this.token);
				}

				return true;
			} catch (error: any) {
				console.error("Error refreshing token:", error);
				// Só desloga em falha de autenticação real (refresh token inválido/expirado).
				// Erros de rede/servidor (timeout, 5xx durante deploy/restart do PM2) são
				// transitórios e NÃO devem encerrar uma sessão ainda válida.
				const status = error?.response?.status;
				if (status === 401 || status === 403) {
					this.logout();
				}
				return false;
			}
		},

		async logout() {
			this.isLoggingOut = true;

			this.token = null;
			this.refreshToken = null;
			this.user = null;
			this.temples = [];

			localStorage.removeItem("token");
			localStorage.removeItem("refreshToken");
			localStorage.removeItem("csrfToken");

			if (router.currentRoute.value.path !== "/login") {
				await router.push("/login");
			}

			this.isLoggingOut = false;
		},

		async fetchUser() {
			try {
				this.loading = true;
				const response = await apiService.get("/auth/me");

				// New format with user and tokens
				if (response.user) {
					this.user = response.user;

					// Update tokens if provided
					if (response.tokens?.access_token) {
						this.token = response.tokens.access_token;
						localStorage.setItem("token", response.tokens.access_token);
					}
					if (response.tokens?.refresh_token) {
						this.refreshToken = response.tokens.refresh_token;
						localStorage.setItem("refreshToken", response.tokens.refresh_token);
					}
				} else if (response.id) {
					// Direct user object response
					this.user = response;
				}

				return this.user;
			} catch (error: any) {
				console.error(
					"Erro ao buscar usuário:",
					error?.response?.data || error
				);

				if (error?.response?.status === 401) {
					this.logout();
				}

				return null;
			} finally {
				this.loading = false;
			}
		},

		async fetchTemples() {
			try {
				const response = await apiService.get("/auth/my-temples");
				this.temples = response.temples || response || [];
			} catch (error) {
				console.error("Erro ao buscar templos:", error);
				this.temples = [];
			}
		},

		/**
		 * Troca o templo do token.
		 *
		 * Por padrão recarrega a rota atual, que é o certo quando a troca parte da
		 * sidebar. Quem chama de uma tela onde não faz sentido continuar depois da
		 * troca — a de assinatura bloqueada — passa `destino`: recarregar
		 * /subscription-blocked deixaria o usuário preso lá, porque nada no router
		 * expulsa dessa rota quando a assinatura está em dia.
		 */
		async switchTemple(templeId: string, destino?: string) {
			try {
				this.loading = true;
				const response = await apiService.post("/auth/switch-temple", {
					templeId,
				});

				if (response.access_token) {
					this.token = response.access_token;
					localStorage.setItem("token", response.access_token);

					if (response.refresh_token) {
						this.refreshToken = response.refresh_token;
						localStorage.setItem("refreshToken", response.refresh_token);
					}
				}

				// Refresh user data for the new temple context
				await this.fetchUser();

				// Reload the current page to refresh data
				if (destino) {
					window.location.href = destino;
				} else {
					router.go(0);
				}

				return true;
			} catch (error: any) {
				console.error("Erro ao trocar de templo:", error);
				return false;
			} finally {
				this.loading = false;
			}
		},

		async initialize() {
			if (this.token) {
				try {
					await this.fetchUser();
					await this.fetchTemples();
				} catch (error) {
					if (this.refreshToken) {
						await this.refreshAccessToken();
					}
				}
			}
		},
	},
});
